/**
 * cloud.maximus.mn-ийн нээлттэй API-аас ирэх төрлүүд.
 *
 * Эх сурвалж: packages/core/website/src/Http/Controllers/Api/*
 */

export const LOCALES = ['mn', 'en', 'ko'] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'mn'

export const LOCALE_LABELS: Record<Locale, string> = {
  mn: 'Монгол',
  en: 'English',
  ko: '한국어'
}

/** 1С-ийн үйлдвэрлэгч — брэнд дээр товч хэлбэрээр */
export type ManufacturerRef = {
  id: string
  name: string
}

/** Үйлдвэрлэгч, холбоотой брэндүүдийнх нь хамт */
export type Manufacturer = ManufacturerRef & {
  product_count: number
  brand_count: number
  brands: { slug: string; name: string; logo_url: string | null }[]
}

export type BrandSummary = {
  slug: string
  name: string
  tagline: string | null
  intro_short: string | null
  logo_url: string | null
  cover_url: string | null

  /** Тухайн брэндийн бараа үйлдвэрлэгчид (1С-ээс). Хуучин API-д байхгүй. */
  manufacturers?: ManufacturerRef[]

  is_featured: boolean
  sort_order: number

  /** Тухайн брэнд аль хэлүүд дээр нийтлэгдсэн бэ */
  available_locales: Locale[]
}

export type BrandDetail = BrandSummary & {
  /** flux:editor-ээс ирсэн HTML */
  intro: string | null
  country: string | null
  founded_year: number | null
  links: {
    website: string | null
    facebook: string | null
    instagram: string | null
    youtube: string | null
  }
  contact: {
    email: string | null
    phone: string | null
  }
  seo: {
    title: string | null
    description: string | null
  }
}

export type Category = {
  slug: string
  name: string
  description: string | null
  image_url: string | null
  sort_order: number
  available_locales: Locale[]
  seo: {
    title: string | null
    description: string | null
  }
}

export type ApiCollection<T> = {
  locale: Locale
  data: T[]
  meta: { total: number }
}

export type ApiResource<T> = {
  locale: Locale
  data: T
}

/** Сайтын глобал тохиргоо — CMS-ийн «Сайтын тохиргоо» хэсгээс */
export type SiteSettings = {
  slogan: string | null
  footer_tagline: string | null
  copyright: string | null
  /** Хөлийн баруун доод бичиг — хоосон бол тольны `footer.note` */
  footer_note?: string | null
  address: string | null
  contact: { phone: string | null; email: string | null; partnership: string | null }
  social: {
    facebook: string | null
    instagram: string | null
    youtube: string | null
    linkedin: string | null
  }
  logo_url: string | null
  logo_white_url: string | null
  og_image_url: string | null
  seo: { title: string | null; description: string | null }
}

/* ── Динамик бүтэц ──────────────────────────────────────────────── */

export type MenuNode = {
  label: string
  description: string | null
  href: string | null
  target: string | null
  children: MenuNode[]
}

/** Блокийн төрөл — frontend дээрх компонентыг сонгоно. */
export type SectionType =
  | 'hero'
  | 'stats'
  | 'coverage'
  | 'distribution'
  | 'brands'
  | 'categories'
  | 'cards'
  | 'portfolio'
  | 'team'
  | 'leadership'
  | 'timeline'
  | 'news'
  | 'b2b'
  | 'contact'
  | 'recruit'
  | 'social'
  | 'richtext'
  | 'faq'
  | 'gallery'
  | 'values'
  | 'purpose'
  | 'showcase'
  | 'product_wall'
  | 'cta'

export type Section = {
  type: SectionType

  /** Хэлнээс хамаарахгүй тохиргоо: layout, эх сурвалж, хязгаар г.м. */
  settings: Record<string, unknown>
  eyebrow: string | null
  heading: string | null
  subheading: string | null
  body: string | null
  cta: { label?: string; url?: string }

  /** Давтагдах агуулга — блокийн төрлөөс хамаарна */
  items: Record<string, unknown>[]
}

export type Page = {
  slug: string
  template: string

  /** Хуудасны дээд banner зураг */
  cover_url: string | null
  title: string | null
  subtitle: string | null
  seo: { title: string | null; description: string | null }
  available_locales: Locale[]
  sections: Section[]
}

export type BrandProduct = {
  id: string | null
  name: string | null
  article: string | null

  /** 1С-ийн дотоод код (жишээ нь OBC1061) */
  internal_code?: string | null

  barcode: string | null
  category: string | null
  category_id: string | null
  country: string | null
  manufacturer: string | null
  image_url: string | null
}

export type Paginated<T> = {
  locale: Locale
  brand: { slug: string; name: string | null }
  data: T[]
  meta: { total: number; page: number; perPage: number; lastPage: number }
}

export type JobPosting = {
  id: number
  title: string | null
  image_url: string | null
  department: string | null
  position: string | null
  location: string | null
  employment_type: string | null
  experience_level: string | null
  vacancies: number | null
  salary: { min: number | null; max: number | null } | null
  published_at: string | null
  deadline_at: string | null

  /** Анкет бөглөх хуудас — cloud дээр */
  apply_url: string
}

export type TeamMemberPublic = {
  name: string
  position: string | null
  avatar_url: string | null
}
